  <template>
  <v-dialog
    :value="value"
    @input="(v) => $emit('input', v)"
    max-width="898"
    transition="dialog-transition"
    scrollable
    persistent
  >
    <v-card color="neutral700" class="pos-r px-16" flat>
      <v-card-title>
        <v-avatar size="24" @click="close" class="close_btn cursor-pointer">
          <img :src="require(`@/assets/icons/close.svg`)" />
        </v-avatar>
      </v-card-title>

      <v-card-text>
        <v-card color="transparent" flat>
          <div class="text-center">
            <p class="text-h5 font-weight-bold">
              {{ $t('ARTWORK.EDIT.COPYWRITING.UPLOAD_3D_STAGE_MODEL') }}
            </p>
            <p>
              {{ $t('ARTWORK.EDIT.COPYWRITING.UPLOAD_A_3D_MODEL_UNIQUE') }}
              <br />
              {{ $t('ARTWORK.EDIT.COPYWRITING.LIKE_3DLOGO_MODEL') }}
            </p>
          </div>

          <v-card-text>
            <SizeBox height="24" />

            <div class="flex-center">
              <label
                @dragover.prevent
                @dragenter="isGrabing = true"
                @drop.prevent="handleDrop"
                @dragend="isGrabing = false"
                @dragleave="isGrabing = false"
                @click="(e) => !!holdingFile && e.preventDefault()"
                :class="!!holdingFile ? 'w90p' : 'w100p'"
                class="cursor-pointer"
                v-show="!imageDataUrl"
              >
                <v-responsive
                  :aspect-ratio="16 / 9"
                  class="elevation-3"
                  contain
                >
                  <div
                    class="file_area fill-all d-flex justify-center align-end"
                    :class="{ isGrabing, isError: !!fileEmptyError }"
                  >
                    <div class="text-center">
                      <v-chip color="neutral400" label>
                        <v-icon class="mr-2">mdi-camera-enhance</v-icon>
                        {{ $t('ARTWORK.EDIT.COPYWRITING.UPLOAD_3D_MODEL') }}
                      </v-chip>
                      <SizeBox height="12" />
                      <p class="text-left">
                        <!-- *3D 模型僅支援 glb、gltf 格式，最大 {{ fileLimit }}MB -->
                        {{
                          $t(
                            'ARTWORK.EDIT.COPYWRITING.SUPPORT_GLB_GLTF_FORMAT_MAXIMUM'
                          )
                        }}
                        {{ fileLimit }}MB
                      </p>
                      <SizeBox height="32" />
                    </div>
                  </div>
                </v-responsive>
                <input
                  ref="fileInput"
                  @change="handleFileChange"
                  type="file"
                  hidden
                  accept=".glb,.gltf"
                />
              </label>
              <div
                v-if="!!imageDataUrl"
                class="flex-center fill-all elevation-4"
              >
                <ModelViewer
                  :model="isGLTF ? holdingFile : imageDataUrl"
                  @error="imageDataUrl = null"
                  content-class="b-rad-16"
                />
              </div>
            </div>
            <div class="text-center" v-show="!!imageDataUrl">
              <SizeBox height="24" />
              <v-btn text @click="imageDataUrl = null">
                <v-icon>mdi-reload</v-icon>
                {{ $t('ARTWORK.EDIT.WORD.UPLOAD_AGAIN') }}
              </v-btn>
            </div>
            <v-card flat color="transparent" min-height="24">
              <v-slide-y-transition>
                <div v-if="!!fileEmptyError" class="flex-center">
                  <span class="error--text">
                    {{ fileEmptyError }}
                  </span>
                </div>
              </v-slide-y-transition>
              <SizeBox height="24" />
            </v-card>

            <v-form ref="form">
              <v-text-field
                :label="$t('ARTWORK.EDIT.WORD.STAGE_MODEL')"
                v-model="form.name"
                :rules="[$rules.required]"
                background-color="neutral700"
                outlined
              ></v-text-field>
            </v-form>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-card-actions class="mb-8">
        <v-row>
          <v-col cols="12" class="flex-center">
            <v-btn width="120" outlined @click="close">
              {{ $t('COMMON.WORD.CANCEL') }}
            </v-btn>
            <SizeBox width="16" />
            <v-btn
              width="120"
              color="primary"
              @click="submit"
              :loading="isSubmiting"
            >
              {{ $t('COMMON.WORD.SAVE_CHANGES') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { addKeyVision3D } from '@/api/panel/exhibitions';

import ModelViewer from '@/components/ModelViewer.vue';
import { addModel } from '@/api/panel/files';

export default {
  components: { ModelViewer },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    workData: {
      type: Object,
      default: null,
      required: true
    }
  },

  data() {
    return {
      isGrabing: false,
      imageDataUrl: null,
      holdingFile: null,
      fileEmptyError: null,
      isSubmiting: false,

      fileLimit: 10,

      form: {
        index: null,
        keyVision3DUrl: null,
        name: ''
      }
    };
  },

  computed: {
    isGLTF() {
      return this?.holdingFile?.name?.endsWith('.gltf');
    }
  },

  watch: {
    value(v) {
      v ? this.init() : this.reset();
    },
    holdingFile(v) {
      if (!v) this.imageDataUrl = null;
    },
    imageDataUrl(v) {
      this.fileEmptyError = null;
      if (!v) {
        this.$refs.fileInput.value = null;
        this.holdingFile = null;
      }
    },
    isGrabing() {
      this.fileEmptyError = null;
    }
  },

  methods: {
    init() {
      if (this.workData.data) {
        this.form.index = this.workData.index;
        this.form.keyVision3DUrl = this.workData.data.keyVision3DUrl;
        this.form.name = this.workData.data.name;

        this.imageDataUrl = this.workData.data.keyVision3DUrl;
      }
    },
    reset() {
      const originData = this.$options.data();
      Object.keys(originData).forEach((key) => {
        this.$set(this.$data, key, originData[key]);
      });
      this.$refs.form.reset();
    },
    close() {
      this.$emit('input', false);
    },
    async submit() {
      let flag = true;
      if (!this.$refs.form.validate()) flag = false;
      if (!this.form.keyVision3DUrl && !this.holdingFile) {
        this.fileEmptyError = this.$t(
          'ARTWORK.EDIT.COPYWRITING.UPLOAD_YOUR_FILE'
        );
        flag = false;
      }

      if (!flag) return;

      this.isSubmiting = true;

      if (this.holdingFile) {
        const formData = new FormData();
        formData.append('file', this.holdingFile);

        const { id, url, error } = await addModel(formData);

        if (error) {
          this.$toast.error(
            `${this.$t('ARTWORK.EDIT.TOAST.ALERT.BUILD_FAIL')} ${error.message}`
          );
          this.isSubmiting = false;
          return;
        }

        this.form.keyVision3DUrl = url;
      }

      this.form.index = this.workData.index;

      const Work = await addKeyVision3D({
        id: this.$route.params.expoId,
        ...this.form
      });

      if (Work.error) {
        this.$toast.error(this.$t('ARTWORK.EDIT.TOAST.ALERT.BUILD_FAIL'));
      } else {
        this.$emit('save', Work);
        this.$toast(this.$t('ARTWORK.EDIT.TOAST.SUCCESS.BUILD_SUCCESSFUL'));
        this.$emit('input', false);
      }

      this.isSubmiting = false;
    },
    handleDrop(e) {
      this.isGrabing = false;
      if (e.dataTransfer.files.length) {
        const file = e.dataTransfer.files[0];
        const exts = ['glb', 'gltf'];

        if (exts.includes(file.name.split('.').pop())) {
          this.handlePreview(file);
        } else {
          this.$toast.error('ARTWORK.EDIT.TOAST.ALERT.FILE_FORMAT_NOT_MATCH');
        }
      }
    },
    handleFileChange(e) {
      if (e.target.files.length) {
        const file = e.target.files[0];
        this.handlePreview(file);
      }
    },

    handlePreview(file) {
      if (file.size > 1048576 * this.fileLimit) {
        this.$toast.error(
          `${this.$t('ARTWORK.EDIT.COPYWRITING.FILE_SIZE_NOT_EXCEED')} ${
            this.fileLimit
          }MB`
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageDataUrl = e.target.result;
        this.holdingFile = file;
      };

      reader.readAsDataURL(file);
    }
  }
};
</script>

<style lang="scss" scoped>
.close_btn {
  position: absolute;
  top: 8px;
  right: 8px;
}

.file_area {
  * {
    user-select: none;
  }
  position: relative;
  border: 1px dashed;
  border-color: $neutral300;
  border-radius: 6px;
  background-color: $neutral700;

  &.isError {
    border-color: $error;
  }

  &.isGrabing {
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      width: calc(100% - 8px);
      height: calc(100% - 8px);
      background-color: rgba($neutral100, $alpha: 0.6);
      border-radius: 6px;
      border: 2px solid $primary;
    }
  }
}

.image_in_frame {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>